import { useEffect, useMemo, useRef, useState } from 'react'

import Modal from 'react-modal'

import { apiGetTokenInfo } from '@src/api/asset.v2.api'
import { getTokenMetadata } from '@src/api/solana.api'
import SearchIcon from '@src/components/icons/Search'
import { CHAIN_IDS, ChainId, ChainName, getChainNameFromChainId } from '@src/configs/chainInfo'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { DISCORD_LINK, TELEGRAM_LINK } from '@src/layouts/Footer'
import DesktopTokenSelector from '@src/pages/Exchange/components/TokenSelectorModal/DesktopTokenSelector'
import MobileTokenSelector from '@src/pages/Exchange/components/TokenSelectorModal/MobileTokenSelector'
import {
    filterBySelectedToken,
    filterSolGasAsset,
    filterTokensByQuery,
    sortTokensByPriority,
} from '@src/pages/Exchange/components/TokenSelectorModal/utils'
import { useAvailableTokens } from '@src/pages/Exchange/hooks/useAvailableTokens'
import analyticsService from '@src/services/analytics'
import { IExchangeData } from '@src/types/exchange'
import { Address, IToken, SolanaAddress, TronAddress } from '@src/types/token'
import { getChecksumAddress, isEVMAddress, isSolanaAddress, isTronAddress } from '@src/utils/address'
import { Balance } from '@src/utils/balance'
import { isFeatureEnabled } from '@src/utils/environment'
import { useConfigStore, useTokensStore } from '@src/zustand_stores'

interface ITokenSelectorModalProps {
    isOpen: boolean
    onClose: () => void
    availableChains: Record<string, any>
    selectedChain: string | number
    setSelectedChain: (__chainId: string | number) => void
    selectedToken?: IToken
    setSelectedToken: (token: IToken) => void
    disabledChains?: number[]
    isSolanaSwap: boolean
    side: 'buy' | 'sell'
    buyData: IExchangeData
    sellData: IExchangeData
}

interface IChainData {
    abbreviation: string
    cctp_id: number
    chainId: number
    gasToken: string
    icon: string
    lz_id: number
    name: string
}

// Helper function to sort chains alphabetically by name
const sortChainsByName = (chains: Record<string, IChainData>): Record<string, IChainData> => {
    return Object.fromEntries(
        Object.entries(chains).sort((a, b) => {
            const chainA = a[1]
            const chainB = b[1]
            // Compare chain names alphabetically
            return chainA.name.localeCompare(chainB.name)
        })
    )
}

const TokenSelectorModal = ({
    isOpen,
    onClose,
    availableChains,
    selectedChain,
    setSelectedChain,
    selectedToken,
    setSelectedToken,
    disabledChains = [],
    isSolanaSwap,
    side,
    buyData,
    sellData,
}: ITokenSelectorModalProps): JSX.Element => {
    // Convert selectedChain to number for consistent comparison
    const selectedChainAsNumber = typeof selectedChain === 'string' ? parseInt(selectedChain) : selectedChain

    // Initialize activeChainSelection with selectedChainAsNumber
    const [activeChainSelection, setActiveChainSelection] = useState<number | undefined>(selectedChainAsNumber)
    const [tokenQuery, setTokenQuery] = useState('')
    const [searchResultsChain, setSearchResultsChain] = useState<Record<string, any>>({})
    const [searchResult, setSearchResult] = useState<IToken[]>([])
    const [highlightedChain, setHighlightedChain] = useState<number>()
    const [isSearchingAllChains, setIsSearchingAllChains] = useState(false)
    const [searchProgress, setSearchProgress] = useState({ completed: 0, total: 0 })
    // Track the temporary chain selection separately from the actual selected chain
    const [temporaryChainSelection, setTemporaryChainSelection] = useState<number>()
    // Track when the modal was opened for duration calculation
    const modalOpenTimestamp = useRef<number>(0)

    // Update activeChainSelection when selectedChain changes
    useEffect(() => {
        if (selectedChainAsNumber) {
            setActiveChainSelection(selectedChainAsNumber)
        }
    }, [selectedChainAsNumber])

    // Track modal open event for analytics
    useEffect(() => {
        if (isOpen) {
            // Store the timestamp when modal opens
            modalOpenTimestamp.current = Date.now()

            // Get connected wallet address if available
            const walletAddress = ''

            // Track token modal open event
            analyticsService.trackTokenModalOpen(
                side === 'sell' ? 'source' : 'destination',
                selectedToken?.symbol,
                selectedChainAsNumber,
                walletAddress
            )
        }
    }, [isOpen, side, selectedToken, selectedChainAsNumber])

    const { tokens: storedTokens } = useTokensStore()
    const { isTronConnected, isSolanaConnected, isEVMConnected } = useWalletConnections()
    const { restrictedChains } = useConfigStore()

    const isMantleEnabled = isFeatureEnabled('ENABLE_MANTLE_CHAIN')
    const isCeloEnabled = isFeatureEnabled('ENABLE_CELO_CHAIN')

    // First get available chains, unsorted
    const availableChainsRaw = useMemo(() => {
        // Start with base filtering for Mantle chain
        let filteredChains = isMantleEnabled
            ? availableChains
            : Object.fromEntries(
                  Object.entries(availableChains).filter(([chainId]) => Number(chainId) !== ChainId.Mantle)
              )

        if (!isCeloEnabled) {
            filteredChains = Object.fromEntries(
                Object.entries(filteredChains).filter(([chainId]) => Number(chainId) !== ChainId.Celo)
            )
        }

        // Filter based on restricted chains from URL if available
        if (restrictedChains && restrictedChains.length > 0) {
            filteredChains = Object.fromEntries(
                Object.entries(filteredChains).filter(([chainId]) => restrictedChains.includes(Number(chainId)))
            )
        }

        // Filter out Bitcoin chain if ENABLE_FEATHER is disabled
        if (!isFeatureEnabled('ENABLE_FEATHER')) {
            filteredChains = Object.fromEntries(
                Object.entries(filteredChains).filter(([chainId]) => Number(chainId) !== ChainId.Bitcoin)
            )
        }

        return filteredChains
    }, [availableChains, isMantleEnabled, isCeloEnabled, restrictedChains])

    // Then sort available chains alphabetically by name
    const availableChainsFiltered = useMemo(() => {
        return sortChainsByName(availableChainsRaw)
    }, [availableChainsRaw])

    const getTokenBalance = (token: IToken) => {
        if (!token.userBalance || !token.userBalanceInUSD) return undefined

        // Only show balances for connected wallets on their respective chains
        if (
            (token.chainId === ChainId.Tron && !isTronConnected) ||
            (token.chainId === ChainId.Solana && !isSolanaConnected) ||
            (!isTronConnected && !isSolanaConnected && !isEVMConnected)
        ) {
            return undefined
        }

        // Format balance with reasonable precision
        const balance = new Balance(token).setValue(token.userBalance)
        const formattedBalance = balance.toNumber()

        // For small balances (less than 0.01), show more decimals to avoid showing 0
        const tokenBalance =
            formattedBalance < 0.0001 ? `<0.0001 ${token.symbol}` : `${formattedBalance.toFixed(4)} ${token.symbol}`

        // Format USD value with exactly 4 decimal places
        const usdValue = token.userBalanceInUSD
        const formattedUSD = usdValue < 0.01 ? '<$0.01' : usdValue < 1 ? usdValue.toFixed(4) : usdValue.toFixed(2)

        return {
            tokenBalance: tokenBalance,
            usdBalance: formattedUSD,
        }
    }

    const onChainClicked = (chainId: number): void => {
        // Get previous chain selection for analytics
        const previousChainId = temporaryChainSelection || activeChainSelection || selectedChainAsNumber

        // Update the temporary chain selection instead of the actual selected chain
        setTemporaryChainSelection(chainId)
        setActiveChainSelection(chainId)
        setHighlightedChain(chainId)

        // Track chain selection in analytics
        analyticsService.trackChainSelected(
            side === 'sell' ? 'source' : 'destination',
            chainId,
            getChainNameFromChainId(chainId),
            previousChainId,
            tokenQuery || undefined
        )

        // For testnet mode, we need to ensure all previous results are cleared
        if (isFeatureEnabled('ENABLE_TESTNETS') || isFeatureEnabled('USE_TESTNETS_ONLY')) {
            setSearchResult([])
            setSearchResultsChain({})
            setTokenQuery('')
        } else if (!tokenQuery) {
            // In non-testnet mode, only clear if we don't have an active search
            setTokenQuery('')
        }
    }

    const onTokenClicked = (token: IToken): void => {
        // Track token selection in analytics
        analyticsService.trackTokenSelection(
            token.symbol,
            token.address,
            token.chainId,
            '', // No wallet address available here
            'standard' // This is a standard token selection
        )

        // Only when a token is clicked, we update both the chain and token selections
        handleClose(token)
    }

    const handleClose = (token?: IToken): void => {
        // Clear all settings when modal is closed except activeChainSelection
        const closingSearchQuery = tokenQuery
        setTokenQuery('')
        setHighlightedChain(undefined)
        setSearchResult([])
        setSearchResultsChain({})
        setTemporaryChainSelection(undefined)

        // Calculate how long the modal was open
        const durationMs = Date.now() - modalOpenTimestamp.current

        // Track modal closed event
        analyticsService.trackTokenModalClosed(
            side === 'sell' ? 'source' : 'destination',
            token?.symbol,
            !!token, // Boolean indicating if a token was selected
            closingSearchQuery,
            durationMs
        )

        if (token) {
            // Make sure we update both chain and token before closing
            setSelectedToken(token)
            setSelectedChain(token.chainId)
            // Update activeChainSelection to match the new chain
            setActiveChainSelection(token.chainId)
        } else {
            // If no token was selected, reset activeChainSelection to the current selectedChain
            setActiveChainSelection(selectedChainAsNumber)
        }

        onClose()
    }

    // Use the temporary chain selection for filtering if available, otherwise fall back to the active chain selection or selected chain
    const { tokens } = useAvailableTokens(
        temporaryChainSelection ?? activeChainSelection ?? selectedChain,
        isSolanaSwap,
        side === 'buy'
    )

    const searchTokenByAddress = async (address: string) => {
        // Reset search state before starting new search
        setSearchProgress({ completed: 0, total: 0 })
        setSearchResult([])
        setSearchResultsChain({})
        setIsSearchingAllChains(true)

        // Determine which networks to search based on address format
        let networksToSearch: ChainName[] = []
        if (isSolanaAddress(address)) {
            networksToSearch = ['solana']
        } else if (isTronAddress(address)) {
            networksToSearch = ['tron']
        } else if (isEVMAddress(address)) {
            // Search all EVM networks
            networksToSearch = Object.keys(CHAIN_IDS).filter(
                network =>
                    CHAIN_IDS[network as ChainName] !== ChainId.Solana &&
                    CHAIN_IDS[network as ChainName] !== ChainId.Tron
            ) as ChainName[]
        } else {
            // Invalid address format
            setIsSearchingAllChains(false)
            setSearchProgress({ completed: 0, total: 0 })
            return
        }

        try {
            setSearchProgress({ completed: 0, total: networksToSearch.length })

            const results: IToken[] = []
            const foundChains: Record<string, any> = {}

            // Create an array of promises to fetch token info for each network in parallel
            const tokenInfoPromises = networksToSearch.map(async networkName => {
                try {
                    const chainId = CHAIN_IDS[networkName]

                    console.log('*** chainId', chainId)
                    console.log('*** networkName', networkName)
                    console.log('*** address', address)
                    console.log('*** disabledChains', disabledChains)

                    // Skip disabled chains
                    if (disabledChains.includes(chainId)) {
                        setSearchProgress(prev => ({ completed: prev.completed + 1, total: prev.total }))
                        return null
                    }

                    const chainName = getChainNameFromChainId(chainId)
                    const tokenInfo = await apiGetTokenInfo(chainName.toLowerCase(), address)

                    setSearchProgress(prev => ({ completed: prev.completed + 1, total: prev.total }))

                    if (tokenInfo) {
                        let tokenAddress = address
                        if (chainId === ChainId.Solana) {
                            tokenAddress = address as SolanaAddress
                        } else if (chainId === ChainId.Tron) {
                            tokenAddress = address as TronAddress
                        } else {
                            tokenAddress = getChecksumAddress(address)
                        }

                        if (!disabledChains.includes(chainId) && availableChainsFiltered[chainId]) {
                            return {
                                tokenInfo,
                                chainId,
                                tokenAddress,
                                networkName,
                            }
                        }
                    }
                } catch (error) {
                    console.error(`Error fetching token info for network ${networkName}:`, error)
                    setSearchProgress(prev => ({ completed: prev.completed + 1, total: prev.total }))
                }
                return null
            })

            const tokenResults = await Promise.all(tokenInfoPromises)

            console.log('*** tokenResults', tokenResults)

            // Process the results
            tokenResults.forEach(result => {
                if (result) {
                    const { tokenInfo, chainId, tokenAddress } = result
                    foundChains[chainId] = availableChainsFiltered[chainId]
                    // For Solana tokens, try to get better logo URL from Jupiter API
                    if (chainId === ChainId.Solana) {
                        // First add the token with default logo
                        const tokenWithDefaultLogo = {
                            symbol: tokenInfo.symbol,
                            name: tokenInfo.name,
                            address: address as Address,
                            decimals: tokenInfo.decimals,
                            chainId: chainId,
                            logoURI: `https://raw.githubusercontent.com/trustwallet/assets/refs/heads/master/blockchains/${getChainNameFromChainId(chainId)}/assets/${tokenAddress}/logo.png`,
                            native: false,
                            wrapped: false,
                            isStableCoin: false,
                        }

                        results.push(tokenWithDefaultLogo)

                        // Try to get better logo URL asynchronously
                        getTokenMetadata(tokenAddress)
                            .then(metadata => {
                                if (metadata?.logoURI) {
                                    // Update the token's logo URL if we found a better one
                                    tokenWithDefaultLogo.logoURI = metadata.logoURI
                                }
                            })
                            .catch(() => {
                                // Keep using the default logo if there's an error
                            })
                    } else {
                        // For non-Solana tokens, use the default TrustWallet URL
                        results.push({
                            symbol: tokenInfo.symbol,
                            name: tokenInfo.name,
                            address: address as Address,
                            decimals: tokenInfo.decimals,
                            chainId: chainId,
                            logoURI: `https://raw.githubusercontent.com/trustwallet/assets/refs/heads/master/blockchains/${getChainNameFromChainId(chainId)}/assets/${tokenAddress}/logo.png`,
                            native: false,
                            wrapped: false,
                            isStableCoin: false,
                        })
                    }
                }
            })

            console.log('*** results', results)

            let filteredResults = filterBySelectedToken(results, selectedToken, side)
            filteredResults = filterSolGasAsset(filteredResults)

            console.log('*** filteredResults', filteredResults)

            setSearchResult(filteredResults)
            const filteredChains = Object.entries(foundChains).reduce(
                (filtered, [id, chain]) => {
                    if (filteredResults.some(token => token.chainId === Number(id))) {
                        filtered[id] = chain
                    }
                    return filtered
                },
                {} as Record<string, any>
            )
            setSearchResultsChain(sortChainsByName(filteredChains))

            // Set active chain selection to match the current token's chain if it's in results,
            // or to the first result's chain if there are results
            if (filteredResults.length > 0) {
                // Get the current token
                const currentToken = side === 'sell' ? sellData.token : buyData.token

                // Check if the current token is in the results
                const currentTokenInResults =
                    currentToken &&
                    filteredResults.some(
                        token =>
                            token.address.toLowerCase() === currentToken.address.toLowerCase() &&
                            token.chainId === currentToken.chainId
                    )

                if (currentTokenInResults && currentToken) {
                    setActiveChainSelection(currentToken.chainId)
                } else if (filteredResults.length === 1) {
                    // If there's only one result, set its chain as active
                    setActiveChainSelection(filteredResults[0].chainId)
                }
                // If there are multiple results and current token is not among them,
                // leave the chain selection undefined to show all results
            }
        } catch (error) {
            console.error('Error in searchTokenByAddress:', error)
        } finally {
            setIsSearchingAllChains(false)
            setSearchProgress({ completed: 0, total: 0 })
        }
    }

    const displayedTokens = useMemo(() => {
        // Initial filtering based on selected token
        let filteredTokens = filterBySelectedToken(tokens, selectedToken, side)

        // Apply the SOL-only native gas asset filter
        filteredTokens = filterSolGasAsset(filteredTokens)

        // Same-chain Solana swaps are now always allowed

        if (!tokenQuery) return filteredTokens

        // Check if the input looks like a token address
        const trimmedQuery = tokenQuery.trim()
        if (
            trimmedQuery &&
            (isEVMAddress(trimmedQuery) || isSolanaAddress(trimmedQuery) || isTronAddress(trimmedQuery))
        ) {
            return [] // Return empty array while searching for token address
        }

        // Use the common filter function to search across tokens
        return filterTokensByQuery(filteredTokens, trimmedQuery, selectedToken, side)
    }, [tokens, tokenQuery, storedTokens, activeChainSelection, selectedToken, side, disabledChains])

    const onTokenSearchChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        const newValue = e.target.value

        // Always clear chain selection and results when starting a new search, clearing an existing search,
        // or changing the search query
        if (tokenQuery !== newValue) {
            setActiveChainSelection(undefined)
            setHighlightedChain(undefined)
            setTemporaryChainSelection(undefined) // Clear temporary chain selection as well
        }

        setTokenQuery(newValue)
        setSearchResultsChain({})

        // Check if the input looks like a token address
        const trimmedQuery = newValue.trim()
        if (
            trimmedQuery &&
            (isEVMAddress(trimmedQuery) || isSolanaAddress(trimmedQuery) || isTronAddress(trimmedQuery))
        ) {
            await searchTokenByAddress(trimmedQuery)
        } else if (trimmedQuery) {
            // For name-based search, use our helper function
            let filteredTokens = filterTokensByQuery(storedTokens, trimmedQuery, selectedToken, side)
            // Apply SOL-only native gas asset filter
            filteredTokens = filterSolGasAsset(filteredTokens)

            // Same-chain Solana swaps are now always allowed

            // Filter out tokens from disabled chains
            filteredTokens = filteredTokens.filter(token => !disabledChains.includes(token.chainId))

            // Get unique chains that have matching tokens
            const relevantChainIds = Array.from(new Set(filteredTokens.map(token => token.chainId)))
            const filteredChains = Object.entries(availableChainsFiltered).reduce(
                (filtered, [id, chain]) => {
                    if (relevantChainIds.includes(Number(id))) {
                        filtered[id] = chain
                    }
                    return filtered
                },
                {} as Record<string, any>
            )

            // Sort tokens using our helper function
            const sortedTokens = sortTokensByPriority(filteredTokens, trimmedQuery)

            // Ensure chains are always sorted alphabetically before setting state
            setSearchResultsChain(sortChainsByName(filteredChains))
            setSearchResult(sortedTokens)
            setIsSearchingAllChains(false)
        } else {
            // Clear results if search is empty
            setSearchResult([])
            setSearchResultsChain({})
            setIsSearchingAllChains(false)
        }
    }

    // Function to handle pasting from clipboard
    const handlePasteAddress = async () => {
        try {
            const clipboardText = await navigator.clipboard.readText()
            if (clipboardText) {
                // When pasting an address, we want to search across all chains initially
                setActiveChainSelection(undefined)
                setTemporaryChainSelection(undefined)
                setHighlightedChain(undefined)

                setTokenQuery(clipboardText)

                // If it's an address, trigger the address search
                const trimmedText = clipboardText.trim()
                if (isEVMAddress(trimmedText) || isSolanaAddress(trimmedText) || isTronAddress(trimmedText)) {
                    await searchTokenByAddress(trimmedText)
                }
            }
        } catch (error) {
            console.error('Failed to read clipboard contents:', error)
        }
    }

    // Check if we have no search results for a non-empty query
    const hasNoSearchResults = useMemo(() => {
        if (!tokenQuery || isSearchingAllChains) return false
        // For address searches
        if (isEVMAddress(tokenQuery.trim()) || isSolanaAddress(tokenQuery.trim()) || isTronAddress(tokenQuery.trim())) {
            return searchResult.length === 0
        }
        // For regular text searches
        return displayedTokens.length === 0 && searchResult.length === 0
    }, [tokenQuery, displayedTokens, searchResult, isSearchingAllChains])

    // Track token search with no results for analytics
    useEffect(() => {
        // Only track when there's an actual search with no results
        // and only after the search is complete (not searching all chains)
        if (hasNoSearchResults && tokenQuery && !isSearchingAllChains) {
            const chainId = temporaryChainSelection || activeChainSelection || 'all'
            analyticsService.trackTokenSearchNoResults(
                side === 'sell' ? 'source' : 'destination',
                tokenQuery.trim(),
                chainId,
                ''
            )
        }
    }, [hasNoSearchResults, tokenQuery, isSearchingAllChains, temporaryChainSelection, activeChainSelection, side])

    // Function to clear search input
    const handleClearSearch = () => {
        // Reset all search-related state
        setTokenQuery('')
        // Reset activeChainSelection to the selected chain
        setActiveChainSelection(selectedChainAsNumber)
        setTemporaryChainSelection(undefined)
        setHighlightedChain(undefined)
        setSearchResult([])
        setSearchResultsChain({})
        setIsSearchingAllChains(false)
        setSearchProgress({ completed: 0, total: 0 })
    }

    // Render "Nothing Found" UI component
    const renderNoResultsFound = () => {
        // Show different UI for sell side
        if (side === 'sell') {
            return (
                <div className='flex h-full flex-col items-center justify-center px-6 py-8 text-center'>
                    <h2 className='mb-6 text-4xl font-bold text-white'>Oops! Nothing found</h2>
                    <p className='mb-8 max-w-md text-lg text-token-modal-label-text'>
                        For token listing requests, join our Telegram or Discord, or send an email to{' '}
                        <a href='mailto:outreach@tristero.com' className='text-white hover:underline'>
                            outreach@tristero.com
                        </a>
                        .
                    </p>
                    <div className='flex gap-4'>
                        <button
                            className='flex items-center gap-2 rounded-lg rounded-md bg-mach-gray px-6 py-3 text-white transition-colors hover:bg-opacity-80'
                            onClick={() => window.open(TELEGRAM_LINK, '_blank')}
                        >
                            Join Telegram
                            <svg
                                width='24'
                                height='24'
                                viewBox='0 0 24 24'
                                fill='white'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM16.64 8.8C16.49 10.38 15.84 14.22 15.51 15.99C15.37 16.74 15.09 16.99 14.83 17.02C14.25 17.07 13.81 16.64 13.25 16.27C12.37 15.69 11.87 15.33 11.02 14.77C10.03 14.12 10.67 13.76 11.24 13.18C11.39 13.03 13.95 10.7 14 10.49C14.0069 10.4476 14.0022 10.4043 13.9865 10.3647C13.9708 10.325 13.9447 10.2903 13.91 10.264C13.83 10.214 13.72 10.235 13.63 10.252C13.51 10.274 12.06 11.25 9.27 13.18C8.86 13.45 8.49 13.58 8.17 13.57C7.81 13.56 7.12 13.36 6.62 13.19C6.01 12.98 5.53 12.87 5.57 12.53C5.59 12.35 5.83 12.17 6.29 11.98C9.3 10.64 11.3 9.76 12.3 9.33C15.18 7.93 15.85 7.69 16.29 7.69C16.38 7.69 16.59 7.71 16.73 7.83C16.84 7.92 16.86 8.05 16.87 8.14C16.87 8.21 16.89 8.46 16.64 8.8Z' />
                            </svg>
                        </button>
                        <button
                            className='flex items-center gap-2 rounded-lg rounded-md bg-mach-gray px-6 py-3 text-white transition-colors hover:bg-opacity-80'
                            onClick={() => window.open(DISCORD_LINK, '_blank')}
                        >
                            Join Discord
                            <svg
                                width='24'
                                height='24'
                                viewBox='0 0 24 24'
                                fill='white'
                                xmlns='http://www.w3.org/2000/svg'
                            >
                                <path d='M19.73 4.87C18.33 4.22 16.85 3.76 15.3 3.5C15.13 3.8 14.94 4.21 14.81 4.53C13.17 4.29 11.55 4.29 9.95 4.53C9.82 4.21 9.62 3.8 9.45 3.5C7.9 3.76 6.41 4.23 5.01 4.88C2.44 8.76 1.75 12.53 2.1 16.24C3.88 17.54 5.6 18.35 7.29 18.89C7.71 18.3 8.08 17.67 8.39 17.01C7.73 16.78 7.11 16.48 6.52 16.12C6.65 16.02 6.77 15.92 6.89 15.81C10.65 17.57 14.75 17.57 18.47 15.81C18.59 15.92 18.71 16.02 18.84 16.12C18.25 16.48 17.62 16.78 16.97 17.01C17.28 17.67 17.65 18.3 18.07 18.89C19.76 18.35 21.48 17.54 23.26 16.24C23.67 11.89 22.57 8.15 19.73 4.87ZM8.75 13.95C7.68 13.95 6.8 12.94 6.8 11.71C6.8 10.48 7.66 9.47 8.75 9.47C9.84 9.47 10.72 10.48 10.7 11.71C10.7 12.94 9.84 13.95 8.75 13.95ZM15.61 13.95C14.54 13.95 13.66 12.94 13.66 11.71C13.66 10.48 14.52 9.47 15.61 9.47C16.7 9.47 17.58 10.48 17.56 11.71C17.56 12.94 16.7 13.95 15.61 13.95Z' />
                            </svg>
                        </button>
                    </div>
                </div>
            )
        }

        // Default UI for buy side
        return (
            <div className='flex h-full flex-col items-center justify-center px-6 py-8 text-center'>
                <h2 className='mb-4 text-4xl font-bold text-white'>Oops! Nothing found</h2>
                <p className='mb-8 max-w-md text-lg text-[#8B8B93]'>
                    Seems like there is no such token available. Please check your address or try another token.
                </p>
                <div className='flex gap-4'>
                    <button
                        className='flex items-center gap-2 rounded-lg rounded-md bg-mach-gray px-6 py-3 text-white transition-colors hover:bg-opacity-80'
                        onClick={() => window.open(TELEGRAM_LINK, '_blank')}
                    >
                        Join Telegram
                        <svg width='24' height='24' viewBox='0 0 24 24' fill='white' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM16.64 8.8C16.49 10.38 15.84 14.22 15.51 15.99C15.37 16.74 15.09 16.99 14.83 17.02C14.25 17.07 13.81 16.64 13.25 16.27C12.37 15.69 11.87 15.33 11.02 14.77C10.03 14.12 10.67 13.76 11.24 13.18C11.39 13.03 13.95 10.7 14 10.49C14.0069 10.4476 14.0022 10.4043 13.9865 10.3647C13.9708 10.325 13.9447 10.2903 13.91 10.264C13.83 10.214 13.72 10.235 13.63 10.252C13.51 10.274 12.06 11.25 9.27 13.18C8.86 13.45 8.49 13.58 8.17 13.57C7.81 13.56 7.12 13.36 6.62 13.19C6.01 12.98 5.53 12.87 5.57 12.53C5.59 12.35 5.83 12.17 6.29 11.98C9.3 10.64 11.3 9.76 12.3 9.33C15.18 7.93 15.85 7.69 16.29 7.69C16.38 7.69 16.59 7.71 16.73 7.83C16.84 7.92 16.86 8.05 16.87 8.14C16.87 8.21 16.89 8.46 16.64 8.8Z' />
                        </svg>
                    </button>
                    <button
                        className='flex items-center gap-2 rounded-lg rounded-md bg-mach-gray px-6 py-3 text-white transition-colors hover:bg-opacity-80'
                        onClick={() => window.open(DISCORD_LINK, '_blank')}
                    >
                        Join Discord
                        <svg width='24' height='24' viewBox='0 0 24 24' fill='white' xmlns='http://www.w3.org/2000/svg'>
                            <path d='M19.73 4.87C18.33 4.22 16.85 3.76 15.3 3.5C15.13 3.8 14.94 4.21 14.81 4.53C13.17 4.29 11.55 4.29 9.95 4.53C9.82 4.21 9.62 3.8 9.45 3.5C7.9 3.76 6.41 4.23 5.01 4.88C2.44 8.76 1.75 12.53 2.1 16.24C3.88 17.54 5.6 18.35 7.29 18.89C7.71 18.3 8.08 17.67 8.39 17.01C7.73 16.78 7.11 16.48 6.52 16.12C6.65 16.02 6.77 15.92 6.89 15.81C10.65 17.57 14.75 17.57 18.47 15.81C18.59 15.92 18.71 16.02 18.84 16.12C18.25 16.48 17.62 16.78 16.97 17.01C17.28 17.67 17.65 18.3 18.07 18.89C19.76 18.35 21.48 17.54 23.26 16.24C23.67 11.89 22.57 8.15 19.73 4.87ZM8.75 13.95C7.68 13.95 6.8 12.94 6.8 11.71C6.8 10.48 7.66 9.47 8.75 9.47C9.84 9.47 10.72 10.48 10.7 11.71C10.7 12.94 9.84 13.95 8.75 13.95ZM15.61 13.95C14.54 13.95 13.66 12.94 13.66 11.71C13.66 10.48 14.52 9.47 15.61 9.47C16.7 9.47 17.58 10.48 17.56 11.71C17.56 12.94 16.7 13.95 15.61 13.95Z' />
                        </svg>
                    </button>
                </div>
            </div>
        )
    }

    // Modal styles (shared between mobile and desktop)
    const modalStyle = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'var(--token-modal-overlay)',
            zIndex: 50,
            padding: '16px',
        },
        content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            transform: 'translate(-50%, -50%)',
            width: '95%',
            maxWidth: '576px',
            height: '680px',
            padding: 0,
            borderWidth: '0px',
            borderRadius: '0.75rem',
            background: 'var(--token-modal-background)',
            overflow: 'hidden',
        },
    }

    return (
        <Modal isOpen={isOpen} onRequestClose={() => handleClose(undefined)} style={modalStyle} ariaHideApp={false}>
            {/* Mobile View - Using the refactored component */}
            <MobileTokenSelector
                tokenQuery={tokenQuery}
                setTokenQuery={setTokenQuery}
                searchResultsChain={searchResultsChain}
                availableChainsFiltered={availableChainsFiltered}
                selectedChain={selectedChain}
                temporaryChainSelection={temporaryChainSelection}
                activeChainSelection={activeChainSelection}
                onChainClicked={onChainClicked}
                handleClearSearch={handleClearSearch}
                handlePasteAddress={handlePasteAddress}
                onClose={() => handleClose(undefined)}
                isSearchingAllChains={isSearchingAllChains}
                hasNoSearchResults={hasNoSearchResults}
                renderNoResultsFound={renderNoResultsFound}
                searchProgress={searchProgress}
                displayedTokens={displayedTokens}
                selectedToken={selectedToken}
                onTokenClicked={onTokenClicked}
                getTokenBalance={getTokenBalance}
                searchResult={searchResult}
                side={side}
                sellData={sellData}
                buyData={buyData}
                disabledChains={disabledChains}
                onTokenSearchChange={onTokenSearchChange}
            />

            {/* Desktop View - Using the refactored component */}
            <DesktopTokenSelector
                tokenQuery={tokenQuery}
                setTokenQuery={setTokenQuery}
                searchResultsChain={searchResultsChain}
                availableChainsFiltered={availableChainsFiltered}
                selectedChain={selectedChain}
                temporaryChainSelection={temporaryChainSelection}
                activeChainSelection={activeChainSelection}
                onChainClicked={onChainClicked}
                handlePasteAddress={handlePasteAddress}
                handleClearSearch={handleClearSearch}
                onClose={() => handleClose(undefined)}
                isSearchingAllChains={isSearchingAllChains}
                hasNoSearchResults={searchResult.length === 0 && tokenQuery.length > 0}
                renderNoResultsFound={renderNoResultsFound}
                searchProgress={searchProgress}
                displayedTokens={displayedTokens}
                selectedToken={selectedToken}
                onTokenClicked={onTokenClicked}
                getTokenBalance={getTokenBalance}
                searchResult={searchResult}
                side={side}
                sellData={sellData}
                buyData={buyData}
                highlightedChain={highlightedChain}
                disabledChains={disabledChains}
                onTokenSearchChange={onTokenSearchChange}
            />
        </Modal>
    )
}

export default TokenSelectorModal
